import { useLocale as useLocaleR40 } from '@/lib/locale';
import React, { useEffect, useState } from 'react';
import Seo from '@/components/Seo';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Loader2, AlertTriangle } from 'lucide-react';
import InvoicePayForm, { InvoiceDiscountOnlyFallback } from '@/components/InvoicePayForm';

/**
 * PayInvoicePage
 * --------------
 * Public landing page parents reach from the email a coach sends with
 * an internal SplashPad invoice. Phase 42.4 — replaced the previous
 * external Stripe Payment Link flow with inline Stripe Elements so
 * recipients pay without leaving SplashPad.
 *
 * Flow:
 *   1. URL is /pay-invoice/:invoice_id?token=<single-use pay_token>
 *   2. GET /public/invoices/:invoice_id/intent?token=...
 *      → returns Stripe client_secret (PaymentIntent on the coach's
 *        connected account) + invoice + coach summary.
 *   3. InvoicePayForm renders Stripe Elements and confirms the PI.
 *   4. POST /public/invoices/:invoice_id/confirm finalizes the row.
 *
 * Error states surface friendly copy:
 *   404 — invoice not found  → "Couldn't find this invoice"
 *   403 — wrong/missing token → "Invalid or expired link"
 *   410 — cancelled by coach  → "Coach cancelled this invoice"
 *   503 — coach Stripe not enabled → "Coach hasn't finished payment setup"
 */
export default function PayInvoicePage() {
    const { t: tr40 } = useLocaleR40();
    const { invoiceId } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [noConnect, setNoConnect] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/public/invoices/${invoiceId}/intent`, { params: { token } })
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => {
                if (!alive) return;
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                if (status === 404) setError("We couldn't find this invoice. Double-check the link or ask your coach to resend it.");
                else if (status === 403) setError(detail || "This invoice link is invalid or expired. Ask your coach to resend it.");
                else if (status === 410) setError(detail || "Your coach cancelled this invoice.");
                else if (status === 503) setNoConnect(true);
                else setError(detail || "Something went wrong loading this invoice.");
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [invoiceId, token]);

    if (loading) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-[#f4f7fb]">
                <Loader2 className="h-6 w-6 animate-spin text-[#00d4ff]" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-[#f4f7fb] p-6">
                <Card className="max-w-md p-6 text-center" data-testid="invoice-pay-error-card">
                    <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-[#ff6b35]" />
                    <h1 className="font-display text-lg font-bold text-[#0a1628]">{tr40("We couldn't open this invoice")}</h1>
                    <p className="mt-2 text-sm text-[#1a3a5c]/80">{error}</p>
                </Card>
            </div>
        );
    }
    if (noConnect) {
        return (
            <div className="min-h-dvh bg-[#f4f7fb] p-4 sm:p-6">
                <InvoiceDiscountOnlyFallback
                    intentUrl={`/public/invoices/${invoiceId}/intent`}
                    intentParams={{ token }}
                />
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="min-h-dvh bg-[#f4f7fb] p-4 sm:p-6">
            <Seo
                title={tr40("Pay your invoice | SplashPad")}
                description={tr40("Securely pay your swim lesson invoice online through SplashPad.")}
                canonical="https://splashpad.pro/pay-invoice"
                noIndex
            />
            <InvoicePayForm
                data={data}
                confirmUrl={`/public/invoices/${invoiceId}/confirm`}
                confirmExtra={{ token }}
                intentUrl={`/public/invoices/${invoiceId}/intent`}
                intentParams={{ token }}
                externalUrl={`/public/invoices/${invoiceId}/request-external`}
                externalExtra={{ token }}
            />
        </div>
    );
}
